import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { useToastStore } from '../store/toastStore';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { FormField } from '../components/ui/FormField';
import { formatPhoneMask, unmaskPhone } from '../utils/phone';
import { StarIcon } from '../components/ui/Icons';

interface FormState {
  name: string;
  email: string;
  phone: string;
  password: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  password?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_COMPLETE_REGEX = /^\(\d{2}\) \d{4,5}-\d{4}$/;

function validateForm(form: FormState): FormErrors {
  const errors: FormErrors = {};

  if (!form.name.trim()) {
    errors.name = 'Nome é obrigatório';
  }

  if (!form.email.trim()) {
    errors.email = 'E-mail é obrigatório';
  } else if (!EMAIL_REGEX.test(form.email)) {
    errors.email = 'Informe um e-mail válido';
  }

  if (!form.phone.trim()) {
    errors.phone = 'Celular é obrigatório';
  } else if (!PHONE_COMPLETE_REGEX.test(form.phone)) {
    errors.phone = 'Informe um celular válido: (44) 99999-9999';
  }

  if (!form.password) {
    errors.password = 'Senha é obrigatória';
  } else if (form.password.length < 6) {
    errors.password = 'A senha deve ter pelo menos 6 caracteres';
  }

  return errors;
}

const BRAND_BENEFITS = [
  { label: 'Roupinhas com carinho', description: 'Cada peça escolhida com amor e cuidado' },
  { label: 'Qualidade garantida', description: 'Tecidos macios e seguros para a pele do seu baby' },
  { label: 'Entrega rápida', description: 'Seu pedido chega com agilidade e segurança' },
];

export function RegisterPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const showToast = useToastStore((s) => s.show);

  const [form, setForm] = useState<FormState>({ name: '', email: '', phone: '', password: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [loading, setLoading] = useState(false);

  const setField = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (touched[field]) {
      setErrors((prev) => ({ ...prev, ...validateForm({ ...form, [field]: e.target.value }) }));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = formatPhoneMask(e.target.value);
    setForm((prev) => ({ ...prev, phone: masked }));
    if (touched.phone) {
      setErrors((prev) => ({ ...prev, ...validateForm({ ...form, phone: masked }) }));
    }
  };

  const handleBlur = (field: keyof FormState) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, ...validateForm(form) }));
  };

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    const allTouched: Partial<Record<keyof FormState, boolean>> = {
      name: true, email: true, phone: true, password: true,
    };
    setTouched(allTouched);

    const validationErrors = validateForm(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    setLoading(true);
    try {
      const data = await authService.register({ ...form, phone: unmaskPhone(form.phone) });
      login(data.accessToken, data.customerId, data.name, data.isAdmin);
      navigate('/');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { errors?: string[] } } };
      showToast(axiosErr?.response?.data?.errors?.[0] ?? 'Não foi possível criar o acesso.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex">
      {/* Painel esquerdo — apresentação da marca (oculto em mobile) */}
      <div
        className="hidden lg:flex flex-col justify-between w-[46%] px-12 py-14"
        style={{ background: 'linear-gradient(145deg, #f9e8d0 0%, #f2dbc0 50%, #e8ccad 100%)' }}
      >
        {/* Logo */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">🦄</span>
            <span className="text-2xl font-bold tracking-wide text-text-dark">BabyChic</span>
          </div>
          <p className="text-sm text-text-medium tracking-widest uppercase font-medium ml-1">
            Vestimos criança como criança!
          </p>
        </div>

        {/* Tagline central */}
        <div className="flex-1 flex flex-col justify-center">
          <h2 className="text-4xl font-bold text-text-dark leading-snug mb-4">
            Looks pensados<br />
            <span className="text-chocolate">com carinho</span><br />
            para quem você ama
          </h2>
          <p className="text-text-medium text-base leading-relaxed max-w-xs">
            Crie sua conta e acompanhe seus pedidos com facilidade, a qualquer hora e em qualquer lugar.
          </p>
        </div>

        {/* Benefícios */}
        <div className="grid grid-cols-1 gap-4">
          {BRAND_BENEFITS.map((b) => (
            <div key={b.label} className="flex items-start gap-3">
              <StarIcon className="text-chocolate mt-0.5 w-3.5 h-3.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-text-dark">{b.label}</p>
                <p className="text-xs text-text-light">{b.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Rodapé do painel */}
        <p className="text-xs text-text-light mt-8">
          © 2025 BabyChic — Todos os direitos reservados
        </p>
      </div>

      {/* Painel direito — formulário */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-champagne-light">
        <div className="w-full max-w-md">
          {/* Logo mobile */}
          <div className="flex lg:hidden items-center gap-2 mb-8 justify-center">
            <span className="text-2xl">🦄</span>
            <span className="text-xl font-bold text-text-dark">BabyChic</span>
          </div>

          {/* Cabeçalho do form */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-text-dark mb-1">Criar sua conta</h1>
            <p className="text-text-light text-sm">
              Preencha os dados abaixo para começar
            </p>
          </div>

          {/* Formulário */}
          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
            <FormField label="Nome completo" required>
              <Input
                value={form.name}
                onChange={setField('name')}
                onBlur={handleBlur('name')}
                placeholder="Maria Silva"
                error={touched.name ? errors.name : undefined}
                autoComplete="name"
              />
            </FormField>

            <FormField label="E-mail" required>
              <Input
                type="email"
                value={form.email}
                onChange={setField('email')}
                onBlur={handleBlur('email')}
                placeholder="maria@email.com"
                error={touched.email ? errors.email : undefined}
                autoComplete="email"
              />
            </FormField>

            <FormField label="Celular" required>
              <Input
                type="tel"
                value={form.phone}
                onChange={handlePhoneChange}
                onBlur={handleBlur('phone')}
                placeholder="(44) 99999-9999"
                error={touched.phone ? errors.phone : undefined}
                autoComplete="tel"
              />
            </FormField>

            <FormField label="Senha" required>
              <Input
                type="password"
                value={form.password}
                onChange={setField('password')}
                onBlur={handleBlur('password')}
                placeholder="Mínimo 6 caracteres"
                error={touched.password ? errors.password : undefined}
                autoComplete="new-password"
              />
            </FormField>

            <Button type="submit" fullWidth loading={loading} size="lg" className="mt-1">
              Criar conta
            </Button>
          </form>

          {/* Link para login */}
          <p className="text-center text-sm text-text-light mt-6">
            Já tem uma conta?{' '}
            <Link to="/login" className="text-chocolate font-medium hover:underline">
              Entrar
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
